import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchList } from 'services/common/react-query-common-service';
import { SALES_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../components/StylishLoader';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const activeDate = new Date(dateStr);
    if (isNaN(activeDate.getTime())) return '';

    const currentDate = new Date();
    const diffTime = currentDate.getTime() - activeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return `${diffDays} days ago`;
    }
  } catch (e) {
    return '';
  }
};

const NoMatches = () => (
  <Box className="no-candidates">
    <Box className="icon-box">
      <svg
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14 3C7.92487 3 3 7.92487 3 14C3 20.0751 7.92487 25 14 25C20.0751 25 25 20.0751 25 14C25 7.92487 20.0751 3 14 3ZM0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 17.3257 26.8404 20.3805 24.9035 22.7822L35.5607 33.4393C36.1464 34.0251 36.1464 34.9749 35.5607 35.5607C34.9749 36.1464 34.0251 36.1464 33.4393 35.5607L22.7822 24.9035C20.3805 26.8404 17.3257 28 14 28C6.26801 28 0 21.732 0 14ZM14 12C14.8284 12 15.5 12.6716 15.5 13.5V20.5C15.5 21.3284 14.8284 22 14 22C13.1716 22 12.5 21.3284 12.5 20.5V13.5C12.5 12.6716 13.1716 12 14 12ZM14 10C15.1046 10 16 9.10457 16 8C16 6.89543 15.1046 6 14 6C12.8954 6 12 6.89543 12 8C12 9.10457 12.8954 10 14 10Z"
          fill="currentColor"
        />
      </svg>
    </Box>
    <Typography className="title">No candidates matched yet</Typography>
    <Typography className="subtitle">
      We couldn&apos;t find any candidates that match this job requirement. Try
      adjusting your search filters or check back later.
    </Typography>
  </Box>
);

interface CandidateMatchesProps {
  jobId?: string;
  score?: string;
  tableType?: string;
  workType?: string | null;
  data?: any;
  isLoading?: boolean;
  onOpenCandidateModal?: (candidateId: string) => void;
}

const CandidateMatches: React.FC<CandidateMatchesProps> = ({
  data,
  isLoading,
  jobId,
  onOpenCandidateModal,
  score,
  tableType,
  workType,
}) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isLocationSort = !workType || workType.toLowerCase() !== 'remote';
  const apiParams = new URLSearchParams({
    skip: '0',
    sourceQueue: 'jobQueue',
    take: '5',
    type: 'matched',
  });

  if (isLocationSort) {
    apiParams.append('sortby', 'location');
  }
  apiParams.append('score', score || '0');

  const { data: internalData, isLoading: internalLoading } = useFetchList<any>(
    ['matched-candidates', jobId, score, workType],
    `${SALES_ENDPOINT.MatchedCandidates}/${jobId}?${apiParams.toString()}`,
    {
      enabled: !!jobId && !data,
      refetchOnMount: 'always',
    },
  );

  const matchedData = data || internalData;
  const loading = isLoading !== undefined ? isLoading : internalLoading;

  if (loading) {
    return (
      <Box sx={{ height: '300px', position: 'relative', width: '100%' }}>
        <StylishLoader mode="table" text="Finding matches..." />
      </Box>
    );
  }

  const candidates = matchedData?.data || [];

  if (candidates.length === 0) {
    return (
      <Box className="matches-content-area">
        <NoMatches />
      </Box>
    );
  }

  const navigateTo = (item: any, jobId: string) => {
    // Determine the base path from current location
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basePath =
      pathSegments.length > 0 ? `/${pathSegments[0]}` : '/business-head';

    const candidateId = item?.id || '';

    navigate(`${basePath}/view-candidate/${String(candidateId)}`, {
      state: {
        Params: {
          candidateInfo: item,
          jobId: jobId, // Keeping item.id as jobId if needed by the page
          score,
          tableType,
        },
      },
    });
  };

  return (
    <Box className="matches-content-area">
      {candidates.map((item: any) => {
        const c = item.candidate;
        // Data mapping to "refill" fields correctly
        const firstName = String(c?.firstName || '');
        const middleName = String(c?.middleName || '');
        const lastName = String(c?.lastName || '');
        const name = `${firstName} ${middleName} ${lastName}`.trim() || '-';

        const matchPercentage = item?.matchPercentage || 0;
        const match = `${String(matchPercentage)}%`;

        const visa = c?.workStatus?.name;

        // Preferred availability logic
        const availability = c?.benchStatus;
        const availabilityDate = c?.benchAvailability;

        // Location formatting
        const locationParts = [c?.city, c?.state, c?.country].filter(Boolean);
        const location = locationParts.join(', ');

        const lastActiveRaw = c?.diceId
          ? c?.diceData?.dateLastActive
          : c?.dateLastActive || c?.ceipalModifiedAt;
        const lastActive = formatDate(lastActiveRaw);

        // Contact details construction (Job Title only)
        const contact = c?.jobTitle || c?.title || '';

        // Skills mapping
        const skills =
          item?.topSkills
            ?.map((s: any) => s)
            .filter(Boolean)
            .join(', ') || '-';

        const cardId = String(item.id);

        return (
          <Box key={cardId} className="candidate-match-card">
            <Box
              className="card-header"
              onClick={() =>
                setExpandedCard(expandedCard === cardId ? null : cardId)
              }>
              <Box className="candidate-info">
                <Box className="badges">
                  <Box
                    className="badge match-percentage"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenCandidateModal) {
                        onOpenCandidateModal(String(item.id));
                      }
                    }}
                    sx={{
                      '&:hover': onOpenCandidateModal ? { opacity: 0.8 } : {},
                      cursor: onOpenCandidateModal ? 'pointer' : 'default',
                    }}>
                    {match}
                  </Box>
                  {visa && <Box className="badge visa-badge">{visa}</Box>}
                  {location && (
                    <Box className="badge location-badge">{location}</Box>
                  )}
                  {lastActive && (
                    <Box className="badge last-active-badge">
                      Active: {lastActive}
                    </Box>
                  )}
                </Box>
                <Box className="name-availability">
                  <span
                    className="candidate-name"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateTo(item, jobId || '');
                    }}
                    style={{ cursor: 'pointer' }}>
                    {name}
                  </span>
                  {availability && (
                    <span className="availability-status">
                      <CheckCircleIcon className="check-circle-icon" />
                      {availability}
                    </span>
                  )}
                  {availabilityDate && (
                    <span className="availability-date">
                      <span className="date-dot">•</span>
                      {availabilityDate}
                    </span>
                  )}
                </Box>
                <Box className="contact-details">{contact}</Box>
              </Box>
              <Box className="expand-icon">
                {expandedCard === cardId ? (
                  <KeyboardArrowUpIcon className="expand-arrow-icon" />
                ) : (
                  <KeyboardArrowDownIcon className="expand-arrow-icon" />
                )}
              </Box>
            </Box>
            {expandedCard === cardId && (
              <Box className="card-expanded">
                <Typography className="skills-title">Top Skills</Typography>
                <Typography className="skills-content">{skills}</Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default CandidateMatches;
